<template>
  <FullCenterLayout class="login-layout">
    <a-row justify="center" class="login-layout__form">
      <a-col
        :xs="24"
        :sm="6"
        :class="[
          'login-layout__form__header',
          showLogoutButton && 'login-layout__form__header--with-logout',
        ]"
      >
        <img
          v-if="showLogo"
          class="login-layout__logo"
          :src="Logo"
          :alt="t('{merchantBrand}', { merchantBrand })"
        />
        <BackButton
          v-if="showLogoutButton"
          @on-back-click="logoutUser"
          :label="t('Log out')"
          :loading="logoutLoading"
          mobile-text-only
        />
      </a-col>
      <a-col :xs="24">
        <HeaderTitle class="mb-30" v-if="title || subtitle">
          <div class="flex items-center">
            <BackButton
              v-if="showBackButton"
              :showBtnText="false"
              @on-back-click="$emit('goBack')"
            />
            {{ title }}
          </div>

          <template #subtitle v-if="subtitle">
            <span class="login-layout__subtitle">
              {{ subtitle }}
            </span>
          </template>
        </HeaderTitle>

        <!-- Slot for alerts such as error alerts (e.g: account has been blocked)  -->
        <slot name="login-alerts">
          <a-alert type="error" v-if="errorMessage" :message="errorMessage" />
        </slot>

        <!-- Slot for Login Forms -->
        <slot name="body"></slot>
      </a-col>
    </a-row>
    <!-- Footer -->
    <div class="login-layout__footer">
      <NonAuthenticatedFooter />
    </div>
  </FullCenterLayout>
</template>

<style lang="scss">
.login-layout {
  .ant-alert-error {
    background-color: rgba(196, 66, 27, 0.1);
    border: 1px solid $error;
    border-radius: 4px;
    .ant-alert-message {
      color: $error;
    }
  }
  .ant-alert-info {
    border: 1px solid $accent;
    border-radius: 4px;
    .ant-alert-message {
      color: $accent;
    }
  }
  // Exception: Override only for Login due to unreadable text
  &__subtitle {
    color: $input-label-color;
    font-size: $heading-6-size;
  }
  &__logo {
    width: 88px;
    height: 33px;
  }
  &__form {
    &__header {
      margin-bottom: $margin-xlg;
      &--with-logout {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
    }
  }

  @media screen and (max-width: $screen-sm) {
    display: block;
  }
}
</style>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import { config } from "@/shared/utils/config";
import { windowSizeSimpleStore } from "@/shared/store/simpleStore/windowSizeData";
import FullCenterLayout from "@/shared/components/layouts/FullCenterLayout.vue";
import NonAuthenticatedFooter from "@/shared/components/layouts/NonAuthenticatedFooter.vue";
import HeaderTitle from "@/shared/components/typography/HeaderTitle.vue";
import Logo from "@/assets/images/LogoWithText.svg";
import BackButton from "@/shared/components/buttons/BackButton.vue";
import useLogout from "@/shared/composables/useLogout";

export default defineComponent({
  emits: ["goBack"],
  setup() {
    const t = useI18n().t;
    const isMobileScreen = computed(() => windowSizeSimpleStore.isMobile);
    const store = useStore();
    const isAuthenticated = computed(
      () => store.state.userInfo.isAuthenticated
    );
    const { logoutUser, logoutLoading } = useLogout();
    const showLogoutButton = computed(
      () => isAuthenticated.value && isMobileScreen.value
    );

    return {
      t,
      isMobileScreen,
      Logo,
      merchantBrand: config.merchantBrand,
      showLogoutButton,
      logoutUser,
      logoutLoading,
    };
  },
  props: {
    activeTab: String,
    errorMessage: String,
    title: String,
    subtitle: String,
    showLogo: Boolean,
    showBackButton: Boolean,
  },
  components: {
    FullCenterLayout,
    NonAuthenticatedFooter,
    HeaderTitle,
    BackButton,
  },
});
</script>
